import { RUN_STATUS, REPLAY_STEP_STATUS } from './constants';
import { extractTestCompleteOverallStatus } from './overallStatusUtils';
import { USER_STOPPED_EXECUTION_MESSAGE } from './planUtils';

export const REPLAY_FAILED_MESSAGE = 'One or more steps failed during replay.';

export function buildReplayPassedMessage(passedCount) {
    return `All ${passedCount} step(s) completed successfully in this run.`;
}

function normalizeReplayStatus(status) {
    if (status === REPLAY_STEP_STATUS.PASSED || status === 'Passed') return 'passed';
    if (status === REPLAY_STEP_STATUS.FAILED || status === 'Failed') return 'failed';
    return null;
}

/**
 * Recording / generation overall outcome for execution UI.
 * Pass and fail copy comes from agent `testComplete` overallStatus; aborted uses predefined copy.
 */
export function resolveRecordingOverallStatus(generationRun) {
    if (!generationRun) return null;

    if (generationRun.stoppedByUser || generationRun.status === RUN_STATUS.STOPPED) {
        return { status: 'partial', message: USER_STOPPED_EXECUTION_MESSAGE };
    }

    if (generationRun.status === RUN_STATUS.FAILED) {
        const error = generationRun.error;
        const message =
            typeof error === 'string'
                ? error
                : error?.message ?? error?.error ?? null;
        return { status: 'failed', message };
    }

    if (
        generationRun.status === RUN_STATUS.COMPLETED ||
        generationRun.status === RUN_STATUS.RUNNING
    ) {
        return extractTestCompleteOverallStatus(generationRun.result);
    }

    return null;
}

/**
 * Replay overall outcome for execution UI.
 * Pass, fail, and aborted all use predefined copy (replay has no saveTestReport overallStatus).
 */
export function resolveReplayOverallStatus(replayRun, replayProgressMap = {}) {
    if (!replayRun) return null;

    if (replayRun.status === RUN_STATUS.STOPPED) {
        return { status: 'partial', message: USER_STOPPED_EXECUTION_MESSAGE };
    }

    const statuses = Object.values(replayProgressMap ?? {});
    const passedCount = statuses.filter((s) => normalizeReplayStatus(s) === 'passed').length;
    const failedCount = statuses.filter((s) => normalizeReplayStatus(s) === 'failed').length;

    if (replayRun.status === RUN_STATUS.FAILED) {
        const rawError = replayRun.error?.error ?? replayRun.error;
        return {
            status: 'failed',
            message:
                typeof rawError === 'string' && rawError.trim()
                    ? rawError.trim()
                    : REPLAY_FAILED_MESSAGE,
        };
    }

    if (failedCount > 0) {
        return { status: 'failed', message: REPLAY_FAILED_MESSAGE };
    }

    if (replayRun.status === RUN_STATUS.COMPLETED && passedCount > 0) {
        return { status: 'passed', message: buildReplayPassedMessage(passedCount) };
    }

    return null;
}

/**
 * Top execution banner copy + styling for Automation Studio execution view.
 */
export function resolveExecutionBannerDisplay({
    overallStatus = null,
    automationError = null,
    isExecuting = false,
    replayExecuting = false,
}) {
    const status = overallStatus?.status ?? null;
    const isPartial = status === 'partial';
    const isFailed = status === 'failed';
    const isPassed = status === 'passed';

    const rawErrorMessage =
        automationError != null
            ? typeof automationError === 'string'
                ? automationError
                : automationError?.message || String(automationError)
            : null;

    let message = null;
    if (isPartial) {
        message = overallStatus?.message ?? USER_STOPPED_EXECUTION_MESSAGE;
    } else if (isPassed || isFailed) {
        message = overallStatus?.message ?? rawErrorMessage ?? null;
    } else {
        message = overallStatus?.message ?? rawErrorMessage ?? null;
    }

    let bannerVariant = null;
    if (isPartial) {
        bannerVariant = 'partial';
    } else if (isPassed) {
        bannerVariant = 'passed';
    } else if (isFailed || rawErrorMessage) {
        bannerVariant = 'failed';
    }

    return {
        message,
        bannerVariant,
        showMessage: !!message && !isExecuting && !replayExecuting,
        isPartial,
        isPassed,
        isFailed,
    };
}
